import React from 'react';

type ComponentImport<T = any> = () => Promise<{ default: React.ComponentType<T> }>;

/**
 * This function wraps a lazy loaded component and attempts to reload the app if the load fails.
 * This is meant to handle the situation that can happen after a new release, when users are still
 * running the old version of the client bundle, that has not been fully loaded. When those users navigate
 * to a new page, their browser will request a new chunk of the bundle, but that chunkfile no longer exists.
 * Reloading the page will get them the updated version of the client, with current references to bundle chunks.
 *
 * @example
 * const MyCoolPage = lazy(() => lazyRetry(() => import('./MyCoolSite/MyCoolPage'), 'MyCoolPage'));
 * @returns a React component, the default export from the imported file
 */
const lazyRetry = <T = any>(
  componentImport: ComponentImport<T>,
  name: string
): Promise<{ default: React.ComponentType<T> }> => {
  return new Promise((resolve, reject) => {
    const hasRefreshed = JSON.parse(window.sessionStorage.getItem(`retry-${name}-refreshed`) || 'false');

    componentImport()
      .then((component) => {
        window.sessionStorage.setItem(`retry-${name}-refreshed`, 'false');
        resolve(component);
      })
      .catch((error: unknown) => {
        if (!hasRefreshed) {
          window.sessionStorage.setItem(`retry-${name}-refreshed`, 'true');
          const url = new URL(window.location.href);
          const params = new URLSearchParams(url.search);
          if (!params.has('nocache')) {
            params.set('nocache', Date.now().toString());
          }
          window.location.href = `${url.pathname}${params.toString() ? '?' + params.toString() : ''}${url.hash || ''}`;
        } else {
          window.sessionStorage.setItem(`retry-${name}-refreshed`, 'false');
          reject(error);
        }
      });
  });
};

export { lazyRetry };
